
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AuthStateType } from "../types/auth-state";

export function useAdminStatus(authState: AuthStateType) {
  const { user, setIsSuperuser, setIsAdmin } = authState;

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user?.id) {
        return;
      }
      
      // Suppress admin/superuser status during impersonation
      if (authState.isImpersonating) {
        setIsSuperuser(false);

        // Read from admin_users directly — same source
        // of truth as the non-impersonation path.
        // profiles.role is unreliable as it can drift.
        const impersonatedUserId = authState.profile?.id;
        if (!impersonatedUserId) {
          setIsAdmin(false);
          return;
        }

        try {
          const { data: adminData } = await supabase
            .from('company_members')
            .select('role, status')
            .eq('user_id', impersonatedUserId)
            .eq('role', 'admin')
            .eq('status', 'active')
            .maybeSingle();

          setIsAdmin(!!adminData);
        } catch {
          setIsAdmin(false);
        }
        return;
      }
      
      try {
        const { data: superuserData, error: superuserError } = await supabase.rpc('is_superuser');
        
        if (superuserError) {
          console.error("Error checking superuser status:", superuserError);
        } else {
          setIsSuperuser(!!superuserData);
        }
        
        const { data: adminData, error: adminError } = await supabase.rpc('is_company_admin' as any);
        
        if (adminError) {
          console.error("Error checking admin status:", adminError);
        } else {
          setIsAdmin(!!adminData);
        }
      } catch (err) {
        console.error("Error checking admin status:", err);
      }
    };

    if (user) {
      checkAdminStatus();
    } else {
      setIsSuperuser(false);
      setIsAdmin(false);
    }
  }, [user, setIsSuperuser, setIsAdmin, authState.isImpersonating, authState.profile?.id]);
}
